import { ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Container, Heading, LinkBox, LinkOverlay, Text } from '@chakra-ui/react';
import { colors } from 'constants/constants';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import NextLink from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { FacilityCarouselProps } from 'types/umbraco';

export default function FacilityCarouselBlock({
  title,
  text,
  facilitys,
}: FacilityCarouselProps): JSX.Element | null {
  const itemCount = facilitys?.length ?? 0;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: itemCount > 1, align: 'start', slidesToScroll: 'auto' },
    [Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );

  const [selectedPage, setSelectedPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedPage(emblaApi.selectedScrollSnap());
    const onReInit = () => {
      setPageCount(emblaApi.scrollSnapList().length);
      onSelect();
    };

    onReInit();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onReInit);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onReInit);
    };
  }, [emblaApi]);

  const scrollToPage = useCallback((page: number) => emblaApi?.scrollTo(page), [emblaApi]);

  if (!itemCount && !title && !text?.markup) {
    return null;
  }

  return (
    <Box py={{ base: 10, md: 20 }} className="umb-facility-carousel-block">
      <Container maxW="7xl">
        <Box display="flex" justifyContent="space-between" alignItems="end" mb={8} gap={4}>
          <Box>
            {text?.markup && (
              <Box mt={3} dangerouslySetInnerHTML={{ __html: text.markup }} />
            )}
          </Box>
       
        </Box>

        <Box position="relative">
          <Box ref={emblaRef} overflow="hidden">
            <Box display="flex" alignItems="stretch" ml={-6}>
              {facilitys?.map((facility) => {
                const image = facility.properties?.image?.[0] ?? facility.image;
                const href = facility.url ?? facility.route?.path ?? '#';

                return (
                  <Box
                    key={facility.id ?? href}
                    pl={6}
                    flex={{
                      base: '0 0 50%',
                      sm: '0 0 33.3333%',
                      md: '0 0 25%',
                      lg: '0 0 20%',
                    }}
                    minW={0}>
                    <LinkBox
                      as={Box}
                      position="relative"
                      h="100%"
                      cursor={href !== '#' ? 'pointer' : 'default'}
                      overflow="hidden"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                      bg="white"
                      boxShadow="0 1px 4px rgba(0, 0, 0, 0.08)"
                      display="flex"
                      flexDirection="column">
                    <Box px={{ base: 4, md: 5 }} pt={{ base: 5, md: 6 }} pb={{ base: 6, md: 7 }}>
                      {facility.countyName && (
                        <Text fontSize="md" lineHeight="short" mb={1}>
                          {facility.countyName}
                        </Text>
                      )}
                      {facility.address && (
                        <Text fontSize="md" lineHeight="short" mb={1}>
                          {facility.address}
                        </Text>
                      )}
                      <Box display="flex" alignItems="center" gap={1} mb={5}>
                        <Heading
                          as="h3"
                          size="sm"
                          lineHeight="short"
                          wordBreak="normal"
                          overflowWrap="normal"
                          w="100%">
                          {href !== '#' ? (
                            <LinkOverlay as={NextLink} href={href} zIndex={1}>
                              {facility.name}
                            </LinkOverlay>
                          ) : (
                            facility.name
                          )}
                        </Heading>
                        <ChevronRightIcon boxSize={6} flexShrink={0} />
                      </Box>
                    </Box>
                      {image?.url && (
                        <Box position="relative" mt="auto" style={{ aspectRatio: '1.4' }}>
                          <Image src={image.url} alt={image.altText ?? facility.name} fill style={{ objectFit: 'cover' }} />
                        </Box>
                      )}
                    </LinkBox>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {itemCount > 1 && (
            <Box
              position="relative"
              zIndex={1}
              mt={5}
              display="flex"
              justifyContent="end"
              alignItems="center"
              gap={2}
              p={1.5}
              borderRadius="full"
              w="fit-content"
              ml="auto">
              {Array.from({ length: pageCount }).map((_, page) => {
                const isActive = page === selectedPage;

                return (
                  <Box
                    key={page}
                    as="button"
                    aria-label={`Gå till sida ${page + 1}`}
                    onClick={() => scrollToPage(page)}
                    w={isActive ? '40px' : '12px'}
                    h={'12px'}
                    borderRadius="full"
                    bg={isActive ? colors.secondary.mediumGrey : colors.secondary.lightGrey}
                    border="none"
                    cursor="pointer"
                    transition="all 0.2s ease"
                    _hover={{ transform: 'scale(1.08)' }}
                    _active={{ transform: 'scale(0.96)' }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}